import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, ArrowLeft, DownloadIcon, X } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import UserDetail from '../../../components/manage-users';
import { useModal } from '../../../hooks/useModal';
import RechargeWallet from '../../../components/RechargeWallet';
import { DownloadUserDetails } from '../../../components/manage-users/downloadUserDetails';
import toast from 'react-hot-toast';
import { useManageUsers } from '../../../hooks/useManageUsers';

const pageSizeOptions = [
  { label: '10', value: '10' },
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
];

interface UserRow {
  signupDate: string;
  crn: string;
  brand: string;
  plan: string;
  billing: string;
  status: 'Active' | 'Inactive';
}

const DocumentViewerModal = ({ userId, close }: { userId: string, close: () => void }) => {
  const { useUserDocumentsQuery } = useManageUsers();
  const { data: docs, isLoading } = useUserDocumentsQuery(userId);

  if (isLoading) return <div className="p-8 text-center text-text font-medium">Loading documents...</div>;

  const hasDocuments = docs && Object.values(docs).some((url) => url !== null && url !== '');

  return (
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-6xl max-h-[92vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-text">User Documents</h2>

      </div>

      {!hasDocuments ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-lg text-gray-500 font-semibold mb-2">No Documents Available</p>
          <p className="text-sm text-gray-400">This user hasn't uploaded any verification documents yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(docs).map(([key, url]: [string, any]) => {
            if (!url) return null;
            return (
              <div key={key} className="border rounded-lg p-4 flex flex-col gap-2 bg-white shadow-sm">
                <p className="font-bold text-gray-800 capitalize border-b pb-2 mb-2">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                <div className="bg-gray-50 h-[65vh] rounded border border-gray-200 p-2 overflow-hidden flex justify-center items-center relative group">
                  {url.toLowerCase().endsWith('.pdf') ? (
                    <iframe src={url} className="w-full h-full rounded" title={key} />
                  ) : (
                    <img src={url} alt={key} className="max-w-full max-h-full object-contain rounded" />
                  )}
                </div>
                <a href={url} target="_blank" rel="noreferrer" className="text-teal text-sm font-bold hover:underline text-center mt-2 flex items-center justify-center gap-1">
                  Open in Full Screen
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function ManageUsersPage() {
  const navigate = useNavigate();
  const { open, close, Modal } = useModal();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Pagination State
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  // API Integration
  const { useUsersQuery, useUpdateUserStatusMutation, useUpdateUserBillingTypeMutation } = useManageUsers();
  const { data: usersData, isLoading } = useUsersQuery(page, size, search);
  const { mutate: updateStatus } = useUpdateUserStatusMutation();
  const { mutate: updateBillingType } = useUpdateUserBillingTypeMutation();

  // Extract the actual user list from the paginated response
  const USERS = usersData?.data || [];
  const totalPages = usersData?.totalPages || 0;
  const totalRecords = usersData?.totalRecords || 0;

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleToggleStatus = (userId: string | number, currentStatusStr: string) => {
    const isActive = currentStatusStr === 'Active';
    const actionText = isActive ? 'deactivate' : 'activate';
    
    setOpenMenu(null);
    open({
      Component: () => (
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm text-center">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isActive ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-500'}`}>
            <span className="text-3xl font-bold">!</span>
          </div>
          <h2 className="text-xl font-bold text-text mb-2">Confirm {isActive ? 'Deactivation' : 'Activation'}</h2>
          <p className="text-sm text-text3 mb-6">Are you sure you want to {actionText} this user?</p>
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1 border-gray-300" onClick={close}>Cancel</Button>
            <Button 
              className={`flex-1 ${isActive ? 'bg-red-600 hover:bg-red-700 text-white border-transparent' : 'bg-green-600 hover:bg-green-700 text-white border-transparent'}`} 
              onClick={() => {
                close();
                updateStatus(
                  { userId, payload: { active: !isActive } },
                  {
                    onSuccess: () => toast.success(`User successfully ${actionText}d`),
                    onError: () => toast.error(`Failed to ${actionText} user`)
                  }
                );
              }}
            >
              Yes, {actionText}
            </Button>
          </div>
        </div>
      ),
      centered: true,
      closable: true,
      className: 'w-full max-w-sm bg-transparent shadow-none'
    });
  };

  const handleToggleBillingType = (userId: string | number, currentBillingTypeStr: string) => {
    const isBilling = currentBillingTypeStr === 'Billing' || currentBillingTypeStr === 'Postpaid';
    const targetType = isBilling ? 'Wallet' : 'Billing';
    
    setOpenMenu(null);
    open({
      Component: () => (
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-teal/10 text-teal">
            <span className="text-3xl font-bold">⇄</span>
          </div>
          <h2 className="text-xl font-bold text-text mb-2">Confirm Move to {targetType}</h2>
          <p className="text-sm text-text3 mb-6">Are you sure you want to move this user to {targetType}?</p>
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1 border-gray-300" onClick={close}>Cancel</Button>
            <Button 
              className="flex-1 bg-teal hover:bg-teal/90 text-white border-transparent" 
              onClick={() => {
                close();
                updateBillingType(
                  { userId, payload: { billingType: targetType } },
                  {
                    onSuccess: () => toast.success(`User moved to ${targetType} successfully`),
                    onError: () => toast.error(`Failed to move user to ${targetType}`)
                  }
                );
              }}
            >
              Yes, Move to {targetType}
            </Button>
          </div>
        </div>
      ),
      centered: true,
      closable: true,
      className: 'w-full max-w-sm bg-transparent shadow-none'
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.action-dropdown')) {
        setOpenMenu(null);
      }
    };
    if (openMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openMenu]);

  if (selectedUserId) {
    return <UserDetail userId={selectedUserId} onBack={() => setSelectedUserId(null)} />;
  }

  const DownloadDetails = () => {
    open({
      Component: () => <DownloadUserDetails />,
      centered: true,
      closable: true,
      className: 'w-full max-w-7xl rounded-2xl',
    });
  };
  return (
    <div className="p-3">
      <h1 className="text-2xl lg:text-3xl font-bold text-text">Manage Users</h1>

      <div className="flex flex-col md:flex-row justify-between md:items-start my-6 gap-4">
        <div className="flex flex-row gap-4 w-full md:max-w-xl items-start">
          <div className="w-full lg:max-w-md">
            <Input
              placeholder="Search by Brand Name or CRN"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button onClick={handleSearch} className="bg-teal text-white">Search</Button>
          {(search || searchInput) && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(0);
              }}
              className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              Reset Filters
            </Button>
          )}
        </div>

        <button
          onClick={DownloadDetails}
          className="flex items-center gap-2 rounded-lg text-white p-2 bg-teal font-medium w-fit"
        >
          {/* <DownloadIcon /> */}
          <p>Download User Details</p>
        </button>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto">
        <div className="space-y-3 min-h-[400px]">
          {/* Detached Teal Table Header Card */}
          <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
            <div className="grid [grid-template-columns:1fr_80px_repeat(6,1fr)] gap-2 xl:gap-4 text-center">
              <div>Signup Date</div>
              <div>CRN No.</div>
              <div>Brand Name</div>
              <div>Business Name</div>
              <div>Active Plan</div>
              <div>Billing Type</div>
              <div>Status</div>
              <div>Action</div>
            </div>
          </div>

          {/* User Row Cards */}
          {isLoading ? (
            <div className="bg-white border rounded-xl py-20 text-center text-text3">
              Loading users...
            </div>
          ) : USERS.length === 0 ? (
            <div className="bg-white border rounded-xl py-20 text-center text-text3">
              No users found
            </div>
          ) : (
            USERS.map((u: any) => (
              <div
                key={u.userId}
                className="bg-card border rounded-xl px-3 xl:px-5 py-3 shadow-sm transition-hover hover:shadow-md"
              >
                <div className="grid [grid-template-columns:1fr_80px_repeat(6,1fr)] gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-center text-center">
                  {/* Signup Date */}
                  <div className="font-medium text-text">{u.signupDate || 'N/A'}</div>

                  {/* CRN Number */}
                  <div>
                    <span
                      onClick={() => setSelectedUserId(u.userId.toString())}
                      className="text-blue-500 underline font-bold cursor-pointer"
                    >
                      {u.crnNumber || 'N/A'}
                    </span>
                  </div>

                  {/* Brand Name */}
                  <div className="font-medium text-text">{u.brandName || 'N/A'}</div>

                  {/* Legal Name */}
                  <div className="font-medium text-text">{u.legalBusinessName || 'N/A'}</div>

                  {/* Active Plan */}
                  <div className="flex flex-col gap-1 items-center justify-center">
                    <span className="px-2.5 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs font-bold border border-blue-100" title="B2C Plan">
                      B2C: {u.activePlan || 'Basic'}
                    </span>
                    {u.activeB2bPlan ? (
                      <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-bold border border-purple-100" title="B2B Plan">
                        B2B: {u.activeB2bPlan}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-gray-400 text-xs">
                        B2B: None
                      </span>
                    )}
                  </div>

                  {/* Billing Type */}
                  <div className="font-medium">
                    {u.billingType === 'Billing' || u.billingType === 'Postpaid' ? 'Billing' : 'Wallet'}
                  </div>

                  {/* Status */}
                  <div className="flex justify-center">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-bold tracking-wider ${u.status === 'Inactive'
                        ? 'bg-red-50 text-red-600 border border-red-100'
                        : 'bg-green-50 text-green-700 border border-green-100'
                        }`}
                    >
                      {u.status || 'N/A'}
                    </span>
                  </div>

                  {/* Action */}
                  <div className="relative flex justify-center action-dropdown">
                    <button
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      onClick={() =>
                        setOpenMenu(openMenu === u.userId.toString() ? null : u.userId.toString())
                      }
                    >
                      <MoreVertical size={18} className="text-gray-500" />
                    </button>

                    {openMenu === u.userId.toString() && (
                      <div className="absolute right-0 top-full mt-2 w-52 bg-white border rounded-xl shadow-xl z-50 py-2 overflow-hidden">
                        <button
                          onClick={() => {
                            setSelectedUserId(u.userId.toString());
                            setOpenMenu(null);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-teal hover:text-white transition-colors font-medium"
                        >
                          Edit Profile
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenu(null);
                            open({
                              Component: () => <DocumentViewerModal userId={u.userId.toString()} close={close} />,
                              centered: true,
                              closable: false,
                              className: 'w-full max-w-6xl rounded-2xl bg-transparent shadow-none',
                            });
                          }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-teal hover:text-white transition-colors font-medium"
                        >
                          View Documents
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenu(null);
                            navigate(`/admin/wallet-transaction?crn=${u.crnNumber || ''}`);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-teal hover:text-white transition-colors font-medium"
                        >
                          Recharge Wallet
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenu(null);
                            navigate(`/admin/manage-users/${u.userId}/rates?type=b2c`);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-teal hover:text-white transition-colors font-medium flex items-center justify-between"
                        >
                          <span>View B2C Rates</span>
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">B2C</span>
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenu(null);
                            navigate(`/admin/manage-users/${u.userId}/rates?type=b2b`);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-teal hover:text-white transition-colors font-medium flex items-center justify-between"
                        >
                          <span>View B2B Rates</span>
                          <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold">B2B</span>
                        </button>
                        <button
                          onClick={() => handleToggleBillingType(u.userId, u.billingType)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-teal hover:text-white transition-colors font-medium"
                        >
                          {u.billingType === 'Billing' || u.billingType === 'Postpaid'
                            ? 'Move to Wallet'
                            : 'Move to Billing'}
                        </button>
                        <hr className="my-1 border-gray-100" />
                        <button
                          onClick={() => handleToggleStatus(u.userId, u.status)}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium">
                          {u.status === 'Active' ? 'Deactivate User' : 'Activate User'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            ))
          )}

          {/* Pagination bar matching Orders pattern */}
          {!isLoading && USERS.length > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-6">
              <div className="text-sm text-gray-600">
                Showing page {page + 1} of {totalPages || 1}
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32">
                  <Select
                    options={pageSizeOptions}
                    value={String(size)}
                    position="top"
                    className="h-10 rounded-xl"
                    onChange={(e: any) => {
                      setSize(Number(e.target.value));
                      setPage(0);
                    }}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                  disabled={page <= 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    setPage((prev) =>
                      Math.min(prev + 1, Math.max(totalPages - 1, 0))
                    )
                  }
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Bottom Padding */}
          <div className="h-10" />
        </div>
      </div>
      <Modal />
    </div>
  );
}
